<template>
    <div class="d-flex flex-row justify-content-center mt-2">
        <nav>
            <ul class="pagination" :class="styleType">
                <li class="page-item page-indicator" v-if="hasPrevPage">
                    <RouterLink class="page-link" :to="prevPageUrl ? parseUrl(prevPageUrl) : { path: $route.path, query: { ...$route.query, page: data.current_page - 1 } }">
                        <i class="bi bi-chevron-left"></i>
                    </RouterLink>
                </li>

                <template v-for="(link, k) in visibleLinks" :key="k">
                    <li v-if="link && link.label" class="page-item text-center" :class="{'active': link.active === true || link.active === 'true' || link.active === 1}">
                        <RouterLink 
                            v-if="link.url" 
                            class="page-link text-center" 
                            :to="parseUrl(link.url)">
                            {{ link.label }}
                        </RouterLink>
                        <span v-else class="page-link text-center" style="cursor: default; pointer-events: none;">
                            {{ link.label }}
                        </span>
                    </li>
                </template>

                <li class="page-item page-indicator" v-if="hasNextPage">
                    <RouterLink class="page-link" :to="nextPageUrl ? parseUrl(nextPageUrl) : { path: $route.path, query: { ...$route.query, page: data.current_page + 1 } }">
                        <i class="bi bi-chevron-right"></i>
                    </RouterLink>
                </li>
            </ul>
            <p class="text-muted text-center" style="font-size: 80%" v-if="totalCount === 0">
                Nenhum registro encontrado
            </p>
            <p class="text-muted text-center" style="font-size: 80%" v-else-if="data.data?.length > 0">
                Exibindo {{ data.data?.length }} de {{ totalCount }} registros
            </p>
        </nav>
    </div>
</template>

<script>
export default {
    name: "Pagination",
    props: {
        data: Object,
        queryParams: Object,
        styleType: String
    },
    computed: {
        visibleLinks() {
            const pages = this.cleanLinks.filter(link => /^\d+$/.test(String(link.label).trim()));
            const current = Number(this.data?.current_page || this.data?.meta?.current_page || 1);
            const first = Number(pages[0]?.label);
            const last = Number(pages[pages.length - 1]?.label);
            const visible = pages.filter(link => {
                const page = Number(link.label);
                return page === first || page === last || Math.abs(page - current) <= 1;
            });

            return visible.reduce((links, link, index) => {
                if (index > 0 && Number(link.label) - Number(visible[index - 1].label) > 1) {
                    links.push({ label: '…', url: null, active: false });
                }
                links.push(link);
                return links;
            }, []);
        },
        totalCount() {
            // Laravel pode retornar total em data.total ou data.meta.total
            if (this.data?.total !== undefined) {
                return this.data.total;
            }
            if (this.data?.meta?.total !== undefined) {
                return this.data.meta.total;
            }
            return this.data?.data?.length || 0;
        },
        hasPrevPage() {
            if (!this.data) return false;
            const current = this.data.current_page || 1;
            return current > 1;
        },
        hasNextPage() {
            if (!this.data) return false;
            const current = this.data.current_page || 1;
            const last = this.data.last_page || 1;
            return current < last;
        },
        prevPageUrl() {
            // Verificar múltiplos formatos possíveis
            let url = this.data?.prev_page_url 
                || this.data?.links?.prev 
                || (this.data?.links && Array.isArray(this.data.links) && this.data.links[0]?.url)
                || null;
            
            // Se não encontrou URL mas há página anterior, construir manualmente
            if (!url && this.hasPrevPage) {
                const query = { ...this.$route.query, page: this.data.current_page - 1 };
                return { path: this.$route.path, query };
            }
            
            // Verificar se é uma string válida e não vazia
            if (!url || url === 'null' || url === '' || url === null) {
                return this.hasPrevPage ? { path: this.$route.path, query: { ...this.$route.query, page: this.data.current_page - 1 } } : null;
            }
            
            return url;
        },
        nextPageUrl() {
            // Verificar múltiplos formatos possíveis
            let url = this.data?.next_page_url 
                || this.data?.links?.next 
                || (this.data?.links && Array.isArray(this.data.links) && this.data.links[this.data.links.length - 1]?.url)
                || null;
            
            // Se não encontrou URL mas há próxima página, construir manualmente
            if (!url && this.hasNextPage) {
                const query = { ...this.$route.query, page: this.data.current_page + 1 };
                return { path: this.$route.path, query };
            }
            
            // Verificar se é uma string válida e não vazia
            if (!url || url === 'null' || url === '' || url === null) {
                return this.hasNextPage ? { path: this.$route.path, query: { ...this.$route.query, page: this.data.current_page + 1 } } : null;
            }
            
            return url;
        },
        cleanLinks() {
            try {
                if (!this.data || !this.data.links) {
                    return [];
                }
                
                let links = [];
                
                // Se links é um array, usar diretamente
                if (Array.isArray(this.data.links)) {
                    links = this.data.links;
                } 
                // Se links é um objeto, tentar converter para array
                else if (typeof this.data.links === 'object' && this.data.links !== null) {
                    links = Object.values(this.data.links);
                }
                // Se não for array nem objeto, retornar vazio
                else {
                    return [];
                }
                
                // Garantir que links é um array válido antes de fazer spread
                if (!Array.isArray(links)) {
                    return [];
                }
                
                // Criar cópia do array e filtrar valores null/undefined
                let cleanLinks = [...links].filter(link => link !== null && link !== undefined);
                
                // Filtrar apenas os links que são números de página (não "Previous" e "Next")
                // Laravel geralmente retorna: [Previous, 1, 2, 3, ..., Next]
                // Vamos manter apenas os que têm label numérico ou são páginas válidas
                cleanLinks = cleanLinks.filter(link => {
                    // Se não tem label, pular
                    if (!link.label) return false;
                    
                    // Se é "Previous" ou "Next", pular (já temos botões separados)
                    const label = String(link.label).trim().toLowerCase();
                    
                    // Verificar várias variações de "Previous" e "Next"
                    const previousLabels = ['previous', 'anterior', '&laquo;', '«', '‹', 'página anterior'];
                    const nextLabels = ['next', 'próximo', '&raquo;', '»', '›', 'próxima página', 'proxima pagina'];
                    
                    if (previousLabels.includes(label)) {
                        return false;
                    }
                    if (nextLabels.includes(label)) {
                        return false;
                    }
                    
                    // Verificar se é HTML entity ou símbolo
                    if (label.includes('&laquo') || label.includes('&raquo') || label.includes('&lsaquo') || label.includes('&rsaquo')) {
                        return false;
                    }
                    
                    // Se é um número ou tem URL válida (e não é null), incluir
                    const isNumber = !isNaN(parseInt(label)) && isFinite(label);
                    if (isNumber) {
                        return true;
                    }
                    
                    // Se tem URL válida e não é um dos labels proibidos, incluir
                    return link.url && link.url !== null;
                });
                
                return cleanLinks;
            } catch (error) {
                console.error('Error in cleanLinks:', error);
                return [];
            }
        },
    },
    methods: {
        parseUrl(url) {
            if (!url) return { path: this.$route.path, query: {} };
            
            // Se já é um objeto (path/query), retornar diretamente
            if (typeof url === 'object' && url.path) {
                return url;
            }
            
            // Se é string, fazer parse
            if (typeof url !== 'string') {
                return { path: this.$route.path, query: {} };
            }
            
            try {
                // Se a URL é relativa, adicionar o origin
                let fullUrl = url;
                if (!url.startsWith('http://') && !url.startsWith('https://')) {
                    fullUrl = window.location.origin + url;
                }
                
                const urlObj = new URL(fullUrl);
                const query = Object.fromEntries(urlObj.searchParams.entries());
                const path = this.$route.path;
                return { path, query };
            } catch (error) {
                console.error('Error parsing URL:', error, url);
                // Fallback: tentar extrair query params manualmente
                const queryMatch = url.match(/\?(.+)$/);
                const query = queryMatch ? Object.fromEntries(
                    queryMatch[1].split('&').map(param => {
                        const [key, value] = param.split('=');
                        return [key, decodeURIComponent(value || '')];
                    })
                ) : {};
                return { path: this.$route.path, query };
            }
        }
    }
}
</script>

<style scoped>
.pagination {
    margin-bottom: 0;
}

.page-link {
    color: #6c757d !important;
    background-color: #fff !important;
    border: 1px solid #dee2e6 !important;
    padding: 0.5rem 0.75rem;
    text-decoration: none;
    display: block;
    min-width: 38px;
    text-align: center;
}

.page-link i {
    color: inherit;
    font-size: 1rem;
}

.page-link:hover {
    color: #780f5a !important;
    background-color: #e9ecef !important;
    border-color: #dee2e6 !important;
}

.page-link:hover i {
    color: #780f5a !important;
}

.page-item.active .page-link,
.page-item.active span.page-link {
    color: #fff !important;
    background-color: #780f5a !important;
    border-color: #780f5a !important;
    z-index: 1;
}

.page-item.active .page-link:hover,
.page-item.active span.page-link:hover {
    color: #fff !important;
    background-color: #780f5a !important;
    border-color: #780f5a !important;
}

.page-item:not(.active) .page-link {
    color: #6c757d !important;
}

.page-item:not(.active) .page-link:hover {
    color: #780f5a !important;
}

.page-item:not(.active) .page-link i {
    color: inherit;
}
</style>
